import { Chevron } from "@components/icons"
import MenuColorSwatches, {
  MenuSwatchProps,
} from "@components/MenuColorSwatches"
import { ButtonContainer } from "@components/MenuItem"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import { body16BoldStyles, smallcapsBold13Styles } from "@constants/typography"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  overflow: hidden;
`

const Label = styled(ButtonContainer)<{ $expanded: boolean }>`
  position: relative;
  padding: 16px 10px 16px 24px;
  color: ${colors.mediumDarkGray};
  border: none;

  ${({ $expanded }) =>
    $expanded &&
    `
      ${body16BoldStyles}
      color: ${colors.black};
  `}
`

const CountWrapper = styled.div`
  position: absolute;
  right: 52px;
  width: 20px;
  height: 20px;
  background-color: ${colors.offBlack};
  border-radius: 50%;
`

const Count = styled.div`
  ${smallcapsBold13Styles}
  padding-top: 2.5px;
  color: ${colors.offWhite};
  font-size: 12px;
  letter-spacing: 0.9px;
`

const IconWrapper = styled.div<{ $rotate?: number }>`
  width: 24px;
  height: 24px;
  margin-right: 5px;
  flex-shrink: 0;
  transition: transform ${animation.quick} ${cubicBezier};
  ${({ $rotate }) => $rotate && `transform: rotate(${$rotate}deg);`}
`

const Content = styled.div<{
  $expanded: boolean
  $expandedHeight: number
}>`
  display: flex;
  position: relative;
  height: ${({ $expanded, $expandedHeight }) =>
    $expanded ? `${$expandedHeight}px` : 0};
  padding: ${({ $expanded }) =>
    $expanded ? "16px 9px 16px 19px" : "0 9px 0 19px"};
  flex-direction: column;
  justify-content: center;
  transition: height ${animation.quick} ${cubicBezier};
  overflow: hidden;

  ${ButtonContainer} {
    padding-left: 32px;
  }
`

export const anchorStylesMenu = `
  display: flex;
  position: absolute;
  width: 100%;
  padding: 12px 0;
  flex-wrap: wrap;
  visibility: hidden;
`

export const anchorStylesMenuItem = `
  width: 40px;
  height: 40px;
`

export const anchorStylesSideNav = `
  display: flex;
  position: absolute;
  width: calc(1.5 / 12 * 100vw);
  min-width: calc(300px - (0.5 / 12 * 100vw));
  padding: 12px 9px 0 19px;
  flex-wrap: wrap;
  visibility: hidden;
`

interface MenuItemColorSwatchesProps {
  activeColorCount: number
  anchorContainerStyles?: string
  anchorId?: string
  anchorItemStyles?: string
  colors: Array<MenuSwatchProps>
  expandByDefault?: boolean
}

const MenuItemColorSwatches: React.FC<MenuItemColorSwatchesProps> = ({
  activeColorCount,
  anchorContainerStyles,
  anchorId,
  anchorItemStyles,
  colors,
  expandByDefault,
}) => {
  const [expanded, setExpanded] = React.useState<boolean>(!!expandByDefault)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const getExpandedHeight = () => {
    const anchor: HTMLElement | null = anchorId
      ? document.getElementById(anchorId)
      : document.getElementsByTagName("body")[0]

    if (!anchor) return 0

    const ruler: any = document.createElement("div")
    ruler.style = anchorContainerStyles
      ? anchorContainerStyles
      : anchorStylesMenu

    colors.forEach(() => {
      const item: any = document.createElement("div")
      item.style = anchorItemStyles ? anchorItemStyles : anchorStylesMenuItem
      ruler.appendChild(item)
    })

    anchor.appendChild(ruler)
    const rulerHeight = ruler.getBoundingClientRect().height
    anchor.removeChild(ruler)
    return rulerHeight
  }

  const [expandedHeight, setExpandedHeight] = React.useState<number>(0)

  React.useEffect(() => {
    setExpandedHeight(getExpandedHeight())
  }, [colors.length])

  return (
    <Container>
      <Label $expanded={expanded} onClick={toggleExpanded}>
        Colors
        {activeColorCount > 0 && (
          <CountWrapper>
            <Count>{activeColorCount}</Count>
          </CountWrapper>
        )}
        <IconWrapper $rotate={expanded ? -90 : 90}>
          <Chevron />
        </IconWrapper>
      </Label>
      <Content $expanded={expanded} $expandedHeight={expandedHeight}>
        <MenuColorSwatches colors={colors} />
      </Content>
    </Container>
  )
}

export default MenuItemColorSwatches
